import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import API, { getFileUrl, uploadAPI } from '../services/api';
import { MessageCircle, Send, Users, Image as ImageIcon, Loader, Edit2, X, AtSign } from 'lucide-react';
import './Chat.css';

export default function Chat() {
  const { user } = useAuth();
  const { socket, fetchUnreadCount } = useChat();
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  
  // @Mention states
  const [groupMembers, setGroupMembers] = useState([]);
  const [mentionState, setMentionState] = useState({
    isOpen: false,
    query: '',
    startIndex: -1,
    cursorPos: -1,
    highlightedIndex: 0,
  });
  const [mentionedUserIds, setMentionedUserIds] = useState([]);

  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mentionDropdownRef = useRef(null);

  // Listen on the shared app socket so the sidebar badge and chat use one connection.
  useEffect(() => {
    if (!socket) return undefined;

    const appendMessage = (message) => {
      let belongsToActive = false;

      if (message.team_id) {
        // Team message logic
        if (message.team_id === activeContact?.team_id) {
          belongsToActive = true;
        } else {
          setContacts(prevContacts => prevContacts.map(c => 
            c.team_id === message.team_id ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c
          ));
        }
      } else {
        // Direct message logic
        if ((message.sender_id === user?.user_id && message.receiver_id === activeContact?.user_id) ||
            (message.sender_id === activeContact?.user_id && message.receiver_id === user?.user_id) ||
            // Also handle if activeContact is missing receiver_id but user_id matches
            (message.sender_id === user?.user_id && !message.receiver_id && !activeContact?.isTeam)) {
          belongsToActive = true;
        } else if (message.sender_id !== user?.user_id) {
          setContacts(prevContacts => prevContacts.map(c => 
            c.user_id === message.sender_id ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c
          ));
        }
      }

      if (belongsToActive) {
        setMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        
        if (message.sender_id !== user?.user_id) {
          socket.emit('mark_read', [message.id]);
          if (fetchUnreadCount) fetchUnreadCount();
        }
      }
    };

    const handleMessageEdited = (editedMessage) => {
      setMessages(prev => prev.map(m => m.id === editedMessage.id ? editedMessage : m));
    };

    socket.on('receive_message', appendMessage);
    socket.on('message_sent', appendMessage);
    socket.on('online_users', setOnlineUserIds);
    socket.on('message_edited', handleMessageEdited);

    return () => {
      socket.off('receive_message', appendMessage);
      socket.off('message_sent', appendMessage);
      socket.off('online_users', setOnlineUserIds);
      socket.off('message_edited', handleMessageEdited);
    };
  }, [socket, user?.user_id, activeContact?.user_id]);

  // Fetch contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await API.get('/chat/contacts');
        if (res.data.success) {
          setContacts(res.data.data);
        }
      } catch (err) {
        console.error('Failed to load contacts', err);
      } finally {
        setLoadingContacts(false);
      }
    };
    fetchContacts();
  }, []);

  // Fetch messages when active contact changes
  useEffect(() => {
    if (!activeContact?.user_id) return;
    
    const fetchMessages = async () => {
      try {
        const res = await API.get(`/chat/messages/${activeContact.user_id}`);
        if (res.data.success) {
          setMessages(res.data.data);

          if (activeContact.unreadCount > 0) {
            setContacts(prev => prev.map(c => c.user_id === activeContact.user_id ? { ...c, unreadCount: 0 } : c));
            setActiveContact(prev => ({ ...prev, unreadCount: 0 }));
            if (fetchUnreadCount) fetchUnreadCount();
          }
        }
      } catch (err) {
        console.error('Failed to load messages', err);
      }
    };
    
    fetchMessages();
  }, [activeContact?.user_id, fetchUnreadCount]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load group members for @mentions whenever activeContact changes
  useEffect(() => {
    if (!activeContact) {
      setGroupMembers([]);
      setMentionState({ isOpen: false, query: '', startIndex: -1, cursorPos: -1, highlightedIndex: 0 });
      return;
    }

    const teamId = activeContact.team_id || (activeContact.user_id && String(activeContact.user_id).startsWith('team_') ? parseInt(String(activeContact.user_id).replace('team_', ''), 10) : null);

    if (activeContact.isTeam || teamId) {
      // 1. Initial check: members directly on activeContact
      if (Array.isArray(activeContact.members) && activeContact.members.length > 0) {
        setGroupMembers(activeContact.members);
      }

      // 2. Fetch using /teams/:id (existing verified endpoint)
      API.get(`/teams/${teamId}`)
        .then(res => {
          if (res.data?.success && res.data.data) {
            const teamData = res.data.data;
            const membersList = [];
            if (teamData.teamLead) {
              membersList.push({
                user_id: teamData.teamLead.user_id,
                name: teamData.teamLead.name,
                role: teamData.teamLead.role || 'Team Lead',
                title: teamData.teamLead.title || 'Lead',
                isLead: true,
              });
            }
            if (Array.isArray(teamData.members)) {
              teamData.members.forEach(m => {
                const u = m.user || m;
                if (u && u.user_id && !membersList.some(x => x.user_id === u.user_id)) {
                  membersList.push({
                    user_id: u.user_id,
                    name: u.name,
                    role: u.role || 'Member',
                    title: u.title || '',
                    isLead: false,
                  });
                }
              });
            }
            if (membersList.length > 0) {
              setGroupMembers(membersList);
            }
          }
        })
        .catch(err => {
          console.warn('Could not load team via /teams/:id, fallback to contacts', err);
          const contactUsers = contacts.filter(c => !c.isTeam).map(c => ({
            user_id: c.user_id,
            name: c.name,
            role: c.role,
            title: c.title,
            isLead: false,
          }));
          if (contactUsers.length > 0) {
            setGroupMembers(contactUsers);
          }
        });
    } else {
      // Direct chat: can mention the other contact
      setGroupMembers([{
        user_id: activeContact.user_id,
        name: activeContact.name,
        role: activeContact.role,
        title: activeContact.title,
        isLead: false,
      }]);
    }

    setMentionState({ isOpen: false, query: '', startIndex: -1, cursorPos: -1, highlightedIndex: 0 });
    setMentionedUserIds([]);
  }, [activeContact?.user_id, activeContact?.team_id, contacts]);

  // Filter group members based on @query (falls back to contacts if team members array is loading)
  const filteredMembers = useMemo(() => {
    if (!mentionState.isOpen) return [];
    const pool = groupMembers.length > 0 ? groupMembers : contacts.filter(c => !c.isTeam);
    const q = (mentionState.query || '').toLowerCase().trim();
    if (!q) return pool;
    return pool.filter(m => 
      m.name?.toLowerCase().includes(q) ||
      m.role?.toLowerCase().includes(q) ||
      m.title?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q)
    );
  }, [mentionState.isOpen, mentionState.query, groupMembers, contacts]);

  // Handle input text and detect when @ is typed
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewMessage(value);

    // Look for @ followed by characters up to cursor
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/(?:^|\s)@([a-zA-Z0-9_.-]*)$/);

    if (atMatch) {
      const query = atMatch[1];
      const symbolIndex = textBeforeCursor.lastIndexOf('@');
      setMentionState({
        isOpen: true,
        query,
        startIndex: symbolIndex,
        cursorPos,
        highlightedIndex: 0,
      });
    } else {
      if (mentionState.isOpen) {
        setMentionState(prev => ({ ...prev, isOpen: false }));
      }
    }
  };

  // Keyboard navigation inside @mention dropdown
  const handleKeyDown = (e) => {
    if (mentionState.isOpen && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionState(prev => ({
          ...prev,
          highlightedIndex: (prev.highlightedIndex + 1) % filteredMembers.length,
        }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionState(prev => ({
          ...prev,
          highlightedIndex: (prev.highlightedIndex - 1 + filteredMembers.length) % filteredMembers.length,
        }));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(filteredMembers[mentionState.highlightedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState(prev => ({ ...prev, isOpen: false }));
        return;
      }
    }
  };

  // Select a member from the dropdown and insert @Name into message
  const selectMention = (member) => {
    if (!member) return;
    const before = newMessage.slice(0, mentionState.startIndex);
    const after = newMessage.slice(mentionState.cursorPos);
    const insertedTag = `@${member.name} `;
    const updated = `${before}${insertedTag}${after}`;

    setNewMessage(updated);
    setMentionedUserIds(prev => Array.from(new Set([...prev, Number(member.user_id)])));
    setMentionState({ isOpen: false, query: '', startIndex: -1, cursorPos: -1, highlightedIndex: 0 });

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newPos = before.length + insertedTag.length;
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeContact || !socket) return;

    if (editingMessageId) {
      socket.emit('edit_message', {
        message_id: editingMessageId,
        content: newMessage.trim(),
      });
      setEditingMessageId(null);
    } else {
      const messageData = {
        sender_id: user.user_id,
        content: newMessage.trim(),
        mentioned_user_ids: mentionedUserIds,
      };

      if (activeContact.isTeam) {
        messageData.team_id = activeContact.team_id;
      } else {
        messageData.receiver_id = activeContact.user_id;
      }

      socket.emit('send_message', messageData);
    }
    setNewMessage('');
    setMentionedUserIds([]);
    setMentionState({ isOpen: false, query: '', startIndex: -1, cursorPos: -1, highlightedIndex: 0 });
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setNewMessage('');
    setMentionState({ isOpen: false, query: '', startIndex: -1, cursorPos: -1, highlightedIndex: 0 });
  };

  const startEditMessage = (msg) => {
    setEditingMessageId(msg.id);
    setNewMessage(msg.content);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeContact || !socket) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAPI.single(formData);
      
      if (res.data.success) {
        const url = res.data.data.url;
        const messageData = {
          sender_id: user.user_id,
          content: `[IMAGE:${url}]`,
        };

        if (activeContact.isTeam) {
          messageData.team_id = activeContact.team_id;
        } else {
          messageData.receiver_id = activeContact.user_id;
        }

        socket.emit('send_message', messageData);
      }
    } catch (err) {
      console.error('File upload failed', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const renderMessageContent = (content) => {
    if (!content) return '';
    const imgMatch = content.match(/^\[IMAGE:(.*)\]$/);
    if (imgMatch) {
      const url = imgMatch[1];
      return (
        <img 
          src={getFileUrl(url)} 
          alt="attachment" 
          className="chat-image-attachment"
          onClick={() => setViewImage(getFileUrl(url))}
          style={{ cursor: 'pointer' }}
        />
      );
    }

    // Collect all known member and contact names
    const allKnownNames = [
      ...groupMembers.map(m => m.name),
      ...contacts.map(c => c.name),
      user?.name
    ].filter(Boolean).map(n => n.trim());
    
    // Deduplicate and sort by length descending so longer names match first
    const uniqueNames = Array.from(new Set(allKnownNames)).sort((a, b) => b.length - a.length);

    let regex;
    if (uniqueNames.length > 0) {
      const escaped = uniqueNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
      regex = new RegExp(`(@(?:${escaped}|[A-Za-z0-9_.-]+))(?=[\\s.,!?;:]|$)`, 'gi');
    } else {
      regex = /(@[A-Za-z0-9_.-]+)(?=[\s.,!?;:]|$)/gi;
    }

    const parts = content.split(regex);
    if (parts.length === 1) return content;

    return parts.map((part, i) => {
      if (part && part.startsWith('@')) {
        const cleanName = part.slice(1).trim().toLowerCase();
        const isSelf = user?.name && cleanName === user.name.toLowerCase();
        return (
          <span
            key={i}
            className={`chat-mention-tag ${isSelf ? 'chat-mention-tag--self' : ''}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Filter messages to only show ones between current user and active contact or active team
  const displayMessages = messages.filter(m => {
    if (activeContact?.isTeam) {
      return m.team_id === activeContact.team_id;
    } else {
      return (m.sender_id === user?.user_id && m.receiver_id === activeContact?.user_id) ||
             (m.sender_id === activeContact?.user_id && m.receiver_id === user?.user_id);
    }
  });

  return (
    <div className="chat-container">
      {/* Sidebar */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <MessageCircle size={24} />
          Team Chat
        </div>
        
        <div className="chat-contact-list">
          {loadingContacts ? (
            <div style={{ padding: '20px', color: 'var(--text-muted)' }}>Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div style={{ padding: '20px', color: 'var(--text-muted)' }}>No contacts available.</div>
          ) : (
            contacts.map(contact => (
              <div 
                key={contact.user_id} 
                className={`chat-contact-item ${activeContact?.user_id === contact.user_id ? 'active' : ''}`}
                onClick={() => setActiveContact(contact)}
              >
                <div className={`contact-avatar ${contact.isTeam ? 'team-avatar' : ''}`}>
                  {contact.isTeam ? <Users size={20} /> : contact.name.charAt(0).toUpperCase()}
                  {!contact.isTeam && onlineUserIds.includes(contact.user_id) && <div className="status-dot online"></div>}
                </div>
                <div className="contact-info">
                  <div className="contact-name">{contact.name}</div>
                  <div className="contact-role">{contact.role} {contact.title ? `• ${contact.title}` : ''}</div>
                </div>
                {contact.unreadCount > 0 && (
                  <div className="chat-contact-badge">
                    {contact.unreadCount > 99 ? '99+' : contact.unreadCount}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      {activeContact ? (
        <div className="chat-main">
          <div className="chat-header">
            <div className={`contact-avatar ${activeContact.isTeam ? 'team-avatar' : ''}`}>
              {activeContact.isTeam ? <Users size={20} /> : activeContact.name.charAt(0).toUpperCase()}
            </div>
            <div className="chat-header-info">
              <h2>{activeContact.name}</h2>
              <p>{activeContact.role} {activeContact.isTeam && groupMembers.length > 0 ? `• ${groupMembers.length + 1} members` : ''}</p>
            </div>
          </div>
          
          <div className="chat-messages">
            {displayMessages.map((msg, index) => {
              const isSent = msg.sender_id === user?.user_id;
              const date = new Date(msg.createdAt || Date.now());
              const timeString = date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const senderName = isSent ? '' : (contacts.find(c => c.user_id === msg.sender_id)?.name || msg.sender_name || 'Someone');
              const isMentioned = Boolean(
                !isSent && (
                  msg.isMention ||
                  (Array.isArray(msg.mentioned_user_ids) && msg.mentioned_user_ids.includes(Number(user?.user_id))) ||
                  (user?.name && msg.content?.toLowerCase().includes(`@${user.name.toLowerCase()}`))
                )
              );
              
              return (
                <div key={msg.id || index} className={`message-wrapper ${isSent ? 'sent' : 'received'} ${isMentioned ? 'message--mentioned' : ''}`}>
                  {!isSent && activeContact?.isTeam && (
                    <div className="message-sender-name" style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', marginLeft: '4px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{senderName}</span>
                      {isMentioned && <span className="mention-badge">@ mentioned you</span>}
                    </div>
                  )}
                  <div className="message-bubble-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: isSent ? 'row-reverse' : 'row' }}>
                    <div className={`message-bubble ${msg.content.match(/^\[IMAGE:(.*)\]$/) ? 'message-bubble-image' : ''} ${isMentioned ? 'message-bubble--mentioned' : ''}`}>
                      {renderMessageContent(msg.content)}
                    </div>
                    {isSent && !msg.content.match(/^\[IMAGE:(.*)\]$/) && (
                      <button 
                        onClick={() => startEditMessage(msg)}
                        className="edit-message-btn"
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#ffffff', cursor: 'pointer', opacity: 0.9, padding: '6px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Edit Message"
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                  </div>
                  <div className="message-time">{timeString}</div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chat-input-area">
            {editingMessageId && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-hover)', padding: '8px 16px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', borderBottom: '1px solid var(--border)', fontSize: '12px', color: 'var(--text-muted)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Edit2 size={12} /> Editing Message</span>
                <button onClick={handleCancelEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={14} /></button>
              </div>
            )}
            <form className="chat-input-form" onSubmit={handleSendMessage} style={editingMessageId ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}}>
              <div className="chat-input-wrapper">
                {mentionState.isOpen && (
                  <div className="mention-dropdown" ref={mentionDropdownRef}>
                    <div className="mention-dropdown-header">
                      <div className="mention-dropdown-title">
                        <AtSign size={14} />
                        <span>Mention {activeContact?.isTeam ? `in ${activeContact.name}` : 'member'}</span>
                      </div>
                      <span className="mention-count-pill">
                        {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'}
                      </span>
                    </div>
                    <div className="mention-dropdown-list">
                      {filteredMembers.length === 0 ? (
                        <div className="mention-empty">
                          No members found matching &ldquo;@{mentionState.query}&rdquo;
                        </div>
                      ) : (
                        filteredMembers.map((member, idx) => {
                          const isOnline = onlineUserIds.includes(member.user_id);
                          const isSelected = idx === mentionState.highlightedIndex;
                          return (
                            <div
                              key={member.user_id}
                              className={`mention-item ${isSelected ? 'selected' : ''}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectMention(member);
                              }}
                            >
                              <div className="mention-avatar">
                                {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                                {isOnline && <span className="mention-online-dot" />}
                              </div>
                              <div className="mention-info">
                                <div className="mention-name">
                                  <span>{member.name}</span>
                                  {member.isLead && <span className="mention-lead-badge">Lead</span>}
                                </div>
                                <div className="mention-role">
                                  {member.role || 'Member'} {member.title ? `• ${member.title}` : ''}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="mention-dropdown-footer">
                      <span>Use <kbd>↑</kbd> <kbd>↓</kbd> to navigate, <kbd>Enter</kbd> to select, <kbd>Esc</kbd> to dismiss</span>
                    </div>
                  </div>
                )}
                <input 
                  type="text"
                  ref={inputRef}
                  className="chat-input"
                  placeholder={editingMessageId ? "Edit your message..." : (activeContact?.isTeam ? "Type a message... (type @ to mention a member)" : "Type a message...")}
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  hidden 
                  accept="image/*"
                  onChange={handleFileUpload} 
                />
                <button 
                  type="button" 
                  className="attach-btn" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0 8px', display: 'flex', alignItems: 'center' }}
                >
                  {uploading ? <Loader size={20} className="spinner" /> : <ImageIcon size={20} />}
                </button>
              </div>
              <button 
                type="submit" 
                className="send-btn" 
                disabled={!newMessage.trim() || uploading}
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="no-chat-selected">
          <div className="no-chat-icon">
            <MessageCircle size={32} />
          </div>
          <h3>Select a conversation</h3>
          <p>Choose a contact from the sidebar to start chatting</p>
        </div>
      )}

      {viewImage && (
        <div 
          className="image-lightbox-overlay"
          onClick={() => setViewImage(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'zoom-out'
          }}
        >
          <img 
            src={viewImage} 
            alt="Expanded view" 
            style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} 
            onClick={(e) => e.stopPropagation()}
          />
          <button 
            style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: 24, cursor: 'pointer' }}
            onClick={() => setViewImage(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
